/*eslint sort-keys: ["error", "asc", { natural: false, caseSensitive: true, minKeys: 2 }]*/

import type { RouteNamedMap } from 'vue-router/auto-routes'

export const ASSET = {
  ARROW_CIRCLE: '/icons/arrow-circle.svg',
  BERKSHIRE_LOGO_TEXT_BA_DARKGRAY: '\\berkshireLogoTextBaDarkGray.svg',
  BERKSHIRE_LOGO_TEXT_WHITE: '\\berkshireLogoTextWhite.svg',
  DOC_TRANSFER_ICON: '/icons/doc-transfer.svg',
  DOC_TRANSFER_ICON_TERTIARY: '/icons/doc-transfer-tertiary.svg',
  GOALS_ICON: '/icons/goals.svg',
  GOALS_ICON_TERTIARY: '/icons/goals-tertiary.svg',
  USER_ROLE_ICON: '/icons/user-role.svg',
}

const currentYear = new Date().getFullYear()

export const COPY = {
  ACTIVE: 'Active',
  ALL_CLIENTS: 'All Clients',
  ALL_PLANS: 'All Plans',
  ALPHA_A_TO_Z: 'Alpha (A to Z)',
  AS_OF: 'As of Current Plan Year',
  AT_LEAST_ONE_REQUIRED: '* At least one service line required.',
  AVAILABLE_SUB_PLANS: 'Available Sub Plans',
  BETTER_ON_LARGE_SCREEN: 'This page is designed for large screens.',
  BREAKOUT_INDIVIDUAL_ACTIONS: 'Breakout by individual Personnel Actions',
  BY_FAVORED_GROUP: 'By favored group',
  BY_RACE_GENDER: 'By race and gender',
  CHANGE_CLIENT_FAILED:
    'Users under Granted Access to do not have access to the new client.  Please remove any invalid users before saving.',
  COPYRIGHT: `© Copyright ${currentYear}`,
  CURRENT_CLIENT: 'Current Client',
  DATA_VIZ_BAR_NO_DATA: 'NO DATA',
  DOWNLOAD: 'Download',
  DUPLICATE_REPORT: 'Duplicate Report',
  EXCLUDED_DISPOSITION_CODES: `Disposition codes marked as "Exclude" are omitted from these results.`,
  EXPIRED_LINK_SUBTITLE_NO_LINK:
    'This link is either invalid, expired, or has already been used.',
  EXPIRED_LINK_SUBTITLE_WITH_LINK:
    'This link is either invalid, expired, or has already been used.  You can send a request for a new link using the button below.',
  EXPIRED_LINK_TITLE: 'INVALID OR EXPIRED LINK',
  INCLUDED_SUB_PLANS: 'Included Sub Plans',
  LOGIN_TO_RESUME:
    'Your session has ended and you have been logged out to protect your information. To resume your use of the Berkshire Client Portal, please login.',
  NEW_USER: 'New User',
  NO_ADVERSE_IMPACT: 'There is no significant adverse impact for this plan.',
  NO_ALL_PLANS: 'There are no plans that match the search.',
  NO_DATA: 'No data available',
  NO_GOALS: 'There are currently no goals for this plan.',
  NO_MATCHING_RECORDS: 'No matching records found',
  NO_PAGES:
    'No pages currently exist for this page. To upload files, at least one page must be added.',
  NO_RECENTLY_VIEWED_PLANS:
    'There are no recently viewed plans that match the search.',
  NO_RESULTS: 'There are currently no results for your search.',
  NO_SECTIONS:
    'No sections currently exist for this page. To upload files for this page, at least one section must be added.',
  OPEN_IN_NEW_TAB: 'Open in new tab',
  PASSWORD_EXPIRED:
    'Your password has expired. Please create a new password below to log in.',
  RECENTLY_VIEWED_PLANS: 'Recently Viewed Plans',
  REGISTRATION_REQUIRED_SUBTITLE:
    'Your account needs to be registered before you can access the requested page. Please click the link below to request a registration link.',
  REGISTRATION_REQUIRED_TITLE: 'REGISTRATION REQUIRED',
  REPORT_EXPORT_CANCELLED: 'Report Export Cancelled',
  REPORT_EXPORT_COMPLETE: 'Report Export Complete',
  REPORT_EXPORT_FAILED: 'Report Export Failed',
  REPORT_EXPORT_INTERRUPTED: 'Report Export was interrupted. Please try again.',
  REPORT_EXPORT_IN_PROGRESS: 'Report Export in Progress',
  REPORT_GROUP_GLOBAL:
    'Note: Report groups are global. Any changes will apply to all plans.',
  SEE_DETAILS: 'See Details',
  SELECTED_USER: 'Selected User',
  SHOW_AS_NUMBERS: 'Show as Numbers',
  SHOW_AS_PERCENTAGES: 'Show as Percentages',
  SUMMARIZE_POSITIVE_AND_NEGATIVE:
    'Summarize by Positive and Negative Personnel Actions',
  TOTAL_NUMBER_OF_SUB_PLANS: 'Total Number of Sub Plans',
  TRY_AGAIN: 'Try Again',
  UNABLE_TO_FETCH_CHART_DATA: 'Unable to get chart data.',
  UNASSIGNED: 'Unassigned',
  UNDER_AUDIT_BADGE: 'OFCCP Audit Plan',
  UPLOAD_ZIP_FILE: 'Upload Zip file',
  VALIDATE_CONTENTS: 'Validate Contents',
  WELCOME_SUBTITLE:
    'Complete your new account by entering your password and logging in.',
  WELCOME_TITLE: 'WELCOME',
  YOU_HAVE_NO_NOTIFICATIONS: 'You have no notifications',
}

export const CTA = {
  ADD: 'Add',
  ADD_ALL: 'ADD ALL',
  ALLOW_ACCESS: 'Allow Access',
  APPLY: 'Apply',
  BACK: 'BACK',
  CANCEL: 'CANCEL',
  CANCEL_EXPORT: 'CANCEL EXPORT',
  CLEAR_ALL: 'CLEAR ALL',
  CLOSE: 'CLOSE',
  CONFIRM: 'CONFIRM',
  CREATE: 'CREATE',
  CREATE_ACCOUNT: 'Create Account',
  CREATE_NEW_CLIENT: 'Create New Client',
  CREATE_NEW_USER: 'Create New User',
  CROP: 'CROP',
  DELETE: 'DELETE',
  DISMISS: 'DISMISS',
  GO_TO: 'Go To',
  IMPORT: 'IMPORT',
  LOGIN: 'LOGIN',
  LOGOUT: 'Logout',
  NEW_PAGE: 'New Page',
  NEW_SECTION: 'Section',
  NEXT: 'NEXT',
  OK: 'Ok',
  REMOVE: 'REMOVE',
  RENAME: 'RENAME',
  REQUEST_ACCESS: 'Request Access',
  REQUEST_NEW_LINK: 'Request New Link',
  REQUEST_REGISTRATION_LINK: 'Request Registration Link',
  RESET: 'Reset',
  RUN_IN_BACKGROUND: 'RUN IN BACKGROUND',
  SAVE: 'SAVE',
  SAVE_AND_CREATE_ANOTHER_USER: 'SAVE & CREATE ANOTHER USER',
  SELECT: 'SELECT',
  STAY_LOGGED_IN: 'Stay logged in',
  TRY_AGAIN: 'TRY AGAIN',
  UPDATE: 'Update',
  UPLOAD: 'UPLOAD',
  VIEW: 'View',
  VIEW_ALL: 'VIEW ALL',
  VIEW_REQUEST: 'View Request',
  VIEW_SETTINGS: 'View Settings',
}

export const DATA_CY = {
  AAP_GLOBAL_SETTINGS: 'global-settings',
  ACTION_TYPE_CHECKBOX: 'action-type-checkbox',
  ACTIVE_REPORT: 'active-report',
  ADVERSE_IMPACT_SETTINGS: 'adverse-impact-settings',
  ALERT_FORM_CLIENTS: 'alert-form-clients',
  ALERT_FORM_END_DATE: 'alert-form-end-date',
  ALERT_FORM_HTML_BODY: 'alert-form-html-body',
  ALERT_FORM_RECURRING_INTERVAL: 'alert-form-recurring-interval',
  ALERT_FORM_REQUIRE_ACKNOWLEDGEMENT: 'alert-form-require-acknowledgement',
  ALERT_FORM_ROLES: 'alert-form-roles',
  ALERT_FORM_SERVICE_LINES: 'alert-form-service-lines',
  ALERT_FORM_START_DATE: 'alert-form-start-date',
  ALERT_FORM_TITLE: 'alert-form-title',
  ALERT_REVIEW_BUTTON: 'alert-review-button',
  ALERT_REVIEW_CONFIRM_CHECKBOX: 'alert-review-confirm-checkbox',
  ALERT_REVIEW_DIALOG: 'alert-review-dialog',
  ALERT_REVIEW_PAGINATION: 'alert-review-pagination',
  ALERT_REVIEW_TITLE: 'alert-review-title',
  ALL_CLIENT_TABLE: 'all-client-table',
  ALTERNATE_SSO: 'alternate-sso',
  APPLICANT_SUMMARY_SETTINGS: 'applicant-summary-settings',
  APPLY: 'apply',
  APPLY_TO_SUBPLANS_CHECKBOX: 'apply-to-subplans-checkbox',
  ARROW: 'arrow',
  AVAILABILITY_SETTINGS: 'availability-settings',
  AVAILABLE_REPORT: 'available-report',
  AVATAR: 'avatar',
  AVATAR_BADGE: 'avatar-badge',
  BACK: 'back',
  BAR_SECTION: 'bar-section',
  BCP_PROVIDER: 'bcp-provider',
  BERKSHIRE_ROLE: 'berkshire-role',
  BOTTOM_FEEDER: 'bottom-feeder',
  BOX: 'box',
  BOX_TITLE: 'box-title',
  BREADCRUMBS: 'breadcrumbs',
  BUBBLE_LIST_ITEM: 'dialog-bubble-list-item',
  BULK_ACTION: 'bulk-action',
  BUTTON: 'button',
  CANCEL: 'cancel',
  CAROUSEL_BUTTON_NEXT: 'carousel-button-next',
  CAROUSEL_BUTTON_PREVIOUS: 'carousel-button-previous',
  CAROUSEL_SECTION: 'carousel-section',
  CHANGE_PASSWORD_ERROR: 'change-password-error',
  CHART_LEGEND: 'chart-legend',
  CHECKBOX: 'checkbox',
  CLEAR: 'clear',
  CLIENT_FORM: 'client-form',
  CLIENT_MANAGEMENT: 'client-management',
  CLIENT_MANAGEMENT_TABLE: 'client-management-table',
  CLIENT_NAME: 'client-name',
  CLIENT_ROLE: 'client-role',
  CLOSE: 'close',
  COLUMN: 'column',
  COMPANY: 'company',
  COMPENSATION_SETTINGS: 'compensation-settings',
  CONFIRM: 'confirm',
  CONTACT_SUPPORT: 'contact-support',
  CONTENT: 'content',
  COUNT: 'count',
  CREATE_NEW_CLIENT: 'create-new-client',
  CREATE_NEW_PAGE: 'create-new-page',
  CREATE_NEW_REPORT_GROUP: 'create-new-report-group',
  CREATE_NEW_REPORT_GROUP_DIALOG: 'create-new-report-group-dialog',
  CREATE_NEW_SECTION: 'create-new-section',
  CREATE_NEW_USER: 'create-new-user',
  CROP: 'crop',
  CROPPER: 'cropper',
  CTA: 'cta',
  CURRENT_CLIENT_TABLE: 'current-client-table',
  CURRENT_PASSWORD: 'current-password',
  CUSTOM_AVATAR: 'custom-avatar',
  DATA_COLLECTION_SETTINGS: 'data-collection-settings',
  DATA_VIZ_BAR_CHART_SELECT: 'data-viz-bar-chart-select',
  DATA_VIZ_CARD: 'data-viz-card',
  DATA_VIZ_CARDS_SECTION: 'data-viz-cards-section',
  DATA_VIZ_CARDS_SELECT: 'data-viz-cards-select',
  DATA_VIZ_DRILLDOWN: 'data-viz-drilldown',
  DATA_VIZ_DRILLDOWN_AI_ACTION_TYPE_SELECT:
    'data-viz-drill-down-action-type-select',
  DATA_VIZ_DRILLDOWN_AI_PLAN_SELECT: 'data-viz-drill-down-plan-select',
  DATA_VIZ_DRILLDOWN_BREADCRUMBS: 'data-viz-drilldown-breadcrumbs',
  DATA_VIZ_DRILLDOWN_CAROUSEL_SELECT: 'data-viz-drilldown-carousel-select',
  DATA_VIZ_DRILLDOWN_TILE: 'data-viz-drilldown-tile',
  DATA_VIZ_OVERVIEW_TILE: 'data-viz-overview-tile',
  DATA_VIZ_STACKED_BAR: 'data-viz-stacked-bar',
  DATE: 'date',
  DEFAULT_AVATAR: 'default-avatar',
  DELETE: 'delete',
  DELETE_FILE: 'delete-file',
  DELETE_FILE_DIALOG: 'delete-file-dialog',
  DELETE_FILE_ERROR: 'delete-file-error',
  DELETE_FILE_ICON: 'delete-file-icon',
  DELETE_PAGE: 'delete-page',
  DELETE_PAGE_DIALOG: 'delete-page-dialog',
  DELETE_PAGE_ERROR: 'delete-page-error',
  DELETE_PAGE_ICON: 'delete-page-icon',
  DELETE_REPORT_GROUP_DIALOG: 'delete-report-group-dialog',
  DELETE_REPORT_GROUP_ICON: 'delete-report-group-icon',
  DELETE_SECTION: 'delete-section',
  DELETE_SECTION_DIALOG: 'delete-section-dialog',
  DELETE_SECTION_ERROR: 'delete-section-error',
  DELETE_SECTION_ICON: 'delete-section-icon',
  DIALOG: 'dialog',
  DIALOG_BUBBLE_LIST_ITEM: 'dialog-bubble-list-item',
  DIALOG_TITLE: 'dialog-title',
  DOCUMENTATION_LINK: 'documentation-link',
  DOCUMENT_CENTER: 'document-center',
  DOCUMENT_CENTER_CREATE_NEW_USER: 'document-center-create-new-user',
  DOCUMENT_CENTER_TABLE: 'document-center-table',
  DOCUMENT_CENTER_UNASSIGN_TABLE: 'document-center-unassign-table',
  DOMAIN: 'domain',
  DONUT_SECTION: 'donut-section',
  DOWNLOAD: 'download',
  DOWNLOAD_ALL: 'download-all',
  DRAWER_EXPANDABLE_ITEM: 'drawer-expandable-item',
  DRAWER_EXPANDABLE_LINK: 'drawer-expandable-link',
  DROPBOX_NAME_INPUT: 'dropbox-name-input',
  DROPDOWN_CONTENT: 'dropdown-content',
  DROPDOWN_TOGGLE: 'dropdown-toggle',
  EDIT_AVATAR_DIALOG: 'edit-avatar-dialog',
  EDIT_BUTTON: 'edit',
  EDIT_FILE_DIALOG: 'edit-file-dialog',
  EDIT_FILE_NAME: 'edit-file-name',
  EDIT_FILE_NAME_ERROR: 'edit-file-name-error',
  EDIT_FILE_NAME_ICON: 'edit-file-name-icon',
  EDIT_FILE_RENAME: 'edit-file-rename',
  EDIT_PAGE_DIALOG: 'edit-page-dialog',
  EDIT_PAGE_NAME: 'edit-page-name',
  EDIT_PAGE_NAME_ERROR: 'edit-page-name-error',
  EDIT_PAGE_NAME_ICON: 'edit-page-name-icon',
  EDIT_PAGE_RENAME: 'edit-page-rename',
  EDIT_PROFILE_ERROR: 'edit-profile-error',
  EDIT_REPORT_GROUP_NAME: 'edit-report-group-name',
  EDIT_REPORT_GROUP_NAME_DIALOG: 'edit-report-group-name-dialog',
  EDIT_REPORT_GROUP_NAME_ICON: 'edit-report-group-name-icon',
  EDIT_SECTION_DIALOG: 'edit-section-dialog',
  EDIT_SECTION_NAME: 'edit-section-name',
  EDIT_SECTION_NAME_ERROR: 'edit-section-name-error',
  EDIT_SECTION_NAME_ICON: 'edit-section-name-icon',
  EDIT_SECTION_RENAME: 'edit-section-rename',
  EMAIL: 'email',
  ENABLE: 'enable',
  ERROR_COUNT: 'error-count',
  ERROR_MESSAGE: 'error-message',
  ESTABLISHMENT: 'establishment',
  EXISTING_WORKS_CLIENT: 'existing-works-client',
  EXISTING_WORKS_CLIENT_TOGGLE: 'existing-works-client-toggle',
  EXPAND_COLLAPSE_ALL: 'expand-collapse-all',
  EXPAND_COLLAPSE_ICON: 'expand-collapse-icon',
  EXPORT: 'export',
  FILE_COUNT: 'file-count',
  FILE_NAME: 'file-name',
  FILE_SIZE: 'file-size',
  FILE_UPLOAD: 'file-upload',
  FILE_UPLOAD_INITIALIZER: 'file-upload-initializer',
  FINISH: 'finish',
  FIRST_NAME: 'first-name',
  FOLDER: 'folder',
  FOLDER_FILE: 'folder-file',
  FOLDER_NAME: 'folder-name',
  FORGOT_PASSWORD: 'forgot-password',
  FORM: 'form',
  FORMAT: 'format',
  GLOBAL_APPLY_TO_SUBPLANS_CHECKBOX: 'global-apply-to-subplans-checkbox',
  HEADER: 'header',
  HEADER_DROPDOWN: 'header-dropdown',
  HEADER_LOGO: 'header-logo',
  HELP_DROPDOWN: 'help-dropdown',
  HISTORY: 'history',
  INFORMATION: 'information',
  INPUT: 'input',
  IS_CONSULTANT_CHECKBOX: 'is-consultant-checkbox',
  JOB_GROUP_ANALYSIS_SETTINGS: 'job-group-analysis-settings',
  LAST_NAME: 'last-name',
  LEFT_RIGHT_CHECKBOX: 'left-right-checkbox',
  LEFT_RIGHT_COLUMN: 'left-right-column',
  LEFT_TAB: 'left-tab',
  LEFT_TABS: 'left-tabs',
  LIST_CHILD_ITEM: 'list-child-item',
  LIST_ITEM: 'list-item',
  LIST_ITEM_TITLE: 'list-item-title',
  LIST_TITLE: 'list-title',
  LOADING: 'loading',
  LOGIN_FORM: 'login-form',
  LOGIN_FORM_ERROR: 'login-form-error',
  LOGIN_SUBMIT: 'login-submit',
  LOGIN_URL: 'login-url',
  LOGOUT: 'logout',
  LOGOUT_URL: 'logout-url',
  MANAGE_ACCESS_BUTTON: 'manage-access-button',
  MANAGE_CLIENT_INFORMATION: 'manage-client-information',
  MANAGE_FEATURE_FLAGS: 'manage-feature-flags',
  MANAGE_PROJECTS_LOCATIONS: 'manage-projects-locations',
  MANAGE_SECURITY: 'manage-security',
  MANAGE_SYSTEM_ALERTS: 'manage-system-alerts',
  MARK_ALL_AS_READ: 'mark-all-as-read',
  MESSAGE: 'message',
  MINI_DONUT_CENTER_COUNT: 'mini-donut-center-count',
  MINI_DONUT_CENTER_LABEL: 'mini-donut-center-label',
  MINI_DONUT_LABEL: 'mini-donut-label',
  MINI_DONUT_LEFT: 'mini-donut-left',
  MINI_DONUT_LEGEND_LEFT: 'mini-donut-legend-left',
  MINI_DONUT_LEGEND_RIGHT: 'mini-donut-legend-right',
  MINI_DONUT_RIGHT: 'mini-donut-right',
  MINI_DONUT_SECTION: 'mini-donut-section',
  MINI_DONUT_SORT_SELECT: 'mini-donut-sort-select',
  MINI_DONUT_SUB_LABEL: 'mini-donut-sub-label',
  MODAL_SAVE: 'modal-save',
  NAME: 'name',
  NAVIGATION_ICON: 'navigation-icon',
  NEW_GROUP_NAME: 'new-group-name',
  NEW_PAGE: 'new-page',
  NEW_PAGE_DIALOG: 'new-page-dialog',
  NEW_PAGE_ERROR: 'new-page-error',
  NEW_PAGE_NAME: 'new-page-name',
  NEW_PASSWORD: 'new-password',
  NEW_SECTION: 'new-section',
  NEW_SECTION_DIALOG: 'new-section-dialog',
  NEW_SECTION_ERROR: 'new-section-error',
  NEW_SECTION_NAME: 'new-section-name',
  NEXT: 'next',
  NOTIFICATIONS_DROPDOWN: 'notifications-dropdown',
  NOTIFICATIONS_DROPDOWN_LIST_ITEM: 'notifications-dropdown-list-item',
  NOTIFICATIONS_DROPDOWN_TOGGLE: 'notifications-dropdown-toggle',
  NOTIFICATION_COUNT: 'notification-count',
  NO_PLAN_BANNER: 'no-plan-banner',
  OPEN_IN_NEW_TAB: 'open-in-new-tab',
  OPTIONS: 'options',
  OVERVIEW_SETTINGS_MODAL: 'overview-settings-modal',
  PAGE_PUBLISH_TOGGLE: 'page-publish-toggle',
  PASSWORD: 'password',
  PASSWORD_CONFIRMATION: 'password-confirmation',
  PASSWORD_EXPIRED: 'password-expired',
  PERCENT: 'percent',
  PERMISSION_DIALOG: 'permission-dialog',
  PHONE: 'phone',
  PLAN_AVATAR: 'plan-avatar',
  PLAN_CODE: 'plan-code',
  PLAN_DATE: 'plan-date',
  PLAN_ID: 'plan-id',
  PLAN_SELECTOR: 'plan-selector',
  PLAN_SUMMARY_SETTINGS: 'plan-summary-settings',
  PLAN_TYPE: 'plan-type',
  PRESET_RECIPIENT: 'preset-recipient',
  PREVIEW_REPORT: 'preview-report',
  PRIVACY_POLICY_LINK: 'privacy-policy-link',
  PROGRESS_BAR: 'progress-bar',
  PROGRESS_TEXT: 'progress-text',
  PROJECT_NAME: 'project-name',
  QR_CODE: 'qr-code',
  RECEIVE_SYSTEM_ALERTS_EMAILS: 'receive-system-alerts-emails',
  RECENT_CLIENT: 'recent-client',
  REMOVE_ALL: 'remove-all',
  REMOVE_FILE: 'remove-file',
  REPORTS_LIST_ITEM: 'reports-list-item',
  REPORTS_LIST_ITEM_CHECKBOX: 'reports-list-item-checkbox',
  REPORTS_LIST_ITEM_HEADER: 'reports-list-item-header',
  REPORTS_LIST_ITEM_HEADER_CHECKBOX: 'reports-list-item-header-checkbox',
  REPORTS_LIST_SECTION: 'reports-list-section',
  REPORTS_MANAGEMENT: 'reports-management',
  REPORT_GROUP: 'report-group',
  REPORT_GROUPED_ICON: 'report-grouped-icon',
  REPORT_GROUPS: 'report-groups',
  REPORT_PREVIEW_FAILURE: 'report-preview-failure',
  REPORT_SETTINGS_MODAL: 'report-settings-modal',
  REQUIRE_SSO: 'require-sso',
  RESEND: 'resend',
  RESET: 'reset',
  RESET_GROUPS: 'reset-groups',
  RESET_GROUPS_EAST: 'reset-groups-east',
  RESET_GROUPS_WEST: 'reset-groups-west',
  RESET_PASSWORD_FORM: 'reset-password-form',
  RESET_REPORT_GROUP_DIALOG: 'reset-report-group-dialog',
  RESOURCE_LIBRARY: 'resource-library',
  RETRY: 'retry',
  RETURN: 'return',
  ROTATE_LEFT: 'rotate-left',
  ROTATE_RIGHT: 'rotate-right',
  RUN_IN_BACKGROUND: 'run-in-background',
  SAVE: 'save',
  SAVE_AND_CREATE_ANOTHER: 'save-and-create-another',
  SAVE_CREATE_ANOTHER_USER: 'save-create-another-user',
  SAVE_PAGE_CHANGES_ERROR: 'save-page-changes-error',
  SAVE_SECTION_FILE_CHANGES_ERROR: 'save-section-file-changes-error',
  SEARCH: 'search',
  SECTION_TITLE: 'section-title',
  SECURITY_SETTINGS: 'security-settings',
  SELECT: 'select',
  SELECTED: 'selected',
  SELECTED_PLANS: 'selected-plans',
  SELECTED_RECORDS: 'selected-records',
  SELECT_ALL: 'select-all',
  SELECT_CLIENT: 'select-client',
  SELECT_ITEM: 'select-item',
  SELECT_TRANSACTION: 'select-transaction',
  SELECT_USER: 'select-user',
  SELECT_USERS: 'select-users',
  SENDER: 'sender',
  SENDER_COMPANY: 'sender-company',
  SENDER_EMAIL: 'sender-email',
  SENDER_NAME: 'sender-name',
  SEPARATOR: 'separator',
  SERVICE_DELIVERABLE_FILE: 'service-deliverable-file',
  SERVICE_DELIVERABLE_PAGE: 'service-deliverable-page',
  SERVICE_DELIVERABLE_SECTION: 'service-deliverable-section',
  SERVICE_DELIVERABLE_TITLE: 'service-deliverable-title',
  SERVICE_LINE_TOGGLE: 'service-line-toggle',
  SERVICE_PERMISSION_TOGGLE: 'service-line-permission',
  SETTINGS: 'settings',
  SHOW_INACTIVE_CLIENTS: 'show-inactive-clients',
  SHOW_INACTIVE_USERS: 'show-inactive-users',
  SHOW_UPLOAD_CERTIFICATE: 'show-upload-certificate',
  SHOW_UPLOAD_METADATA: 'show-upload-metadata',
  SIGN_ASSERTION: 'sign-assertion',
  SIGN_REQUEST: 'sign-request',
  SLIDER: 'slider',
  STATUS: 'status',
  STAY_LOGGED_IN: 'stay-logged-in',
  STEP_TITLE: 'step-title',
  SUBJECT: 'subject',
  SUBMIT: 'submit',
  SUBPLAN_SELECTOR: 'subplan-selector',
  SUBTITLE: 'subtitle',
  SUPPORT_ACCESS: 'support-access',
  SWITCH_CLIENT: 'switch-client',
  SWITCH_CLIENT_TABLE: 'switch-client-table',
  SYSTEM_ALERT_CREATE_BTN: 'system-alert-create-btn',
  TABLE: 'table',
  TIME: 'time',
  TIMEOUT_DIALOG: 'timeout-dialog',
  TITLE: 'title',
  TOGGLE: 'toggle',
  TOOLTIP: 'tooltip',
  TOP_NAVIGATION_LINK: 'top-navigation-link',
  TOP_TAB: 'top-tab',
  TOP_TABS: 'top-tabs',
  TOTAL_RECORDS: 'total-records',
  TRIGGER: 'trigger',
  TYPE: 'type',
  UNDER_AUDIT_BADGE: 'under-audit-badge',
  UNREAD: 'unread',
  UPDATE_AVATAR: 'update-avatar',
  UPDATE_PASSWORD: 'update-password',
  UPDATE_PASSWORD_ICON: 'update-password-icon',
  UPDATE_PLAN: 'update-plan',
  UPLOAD: 'upload',
  UPLOAD_CERTIFICATE: 'upload-certificate',
  UPLOAD_METADATA_FILE: 'upload-certificate',
  UPLOAD_MORE: 'upload-more',
  UPLOAD_ZIP_FILE_DRAWER: 'upload-zip-file-dialog',
  USER_ACCESS_ITEM: 'user-access-item',
  USER_DETAILS: 'user-details',
  USER_EMAIL: 'user-email',
  USER_FORM_TABLE: 'user-form-table',
  USER_ITEM: 'user-item',
  USER_LIST: 'user-list',
  USER_MANAGEMENT: 'user-management',
  USER_MANAGEMENT_TABLE: 'user-management-table',
  USER_NAME: 'user-name',
  USER_PROFILE: 'user-profile',
  USE_ALTERNATE_SSO: 'use-alternate-sso',
  VERIFY_ZIP_FILE: 'verify-zip-item',
  VERIFY_ZIP_ITEM: 'verify-zip-item',
  VIEWABLE_BY_ROLE_ICON: 'viewable-by-role-icon',
  VIEWABLE_BY_ROLE_LIST: 'viewable-by-role-list',
  VIEW_AS: 'view-as',
  VIEW_AS_TABLE: 'view-as-table',
  WFA_PROVIDER: 'wfa-provider',
  WFA_RESULTS_PROVIDER: 'wfa-results-provider',
  WORKFORCE_ANALYSIS_SETTINGS: 'workforce-analysis-settings',
  X_STAY_LOGGED_IN: 'x-stay-logged-in',
  YOU_ARE_VIEWING_AS: 'you-are-viewing-as',
}

export const ERROR = {
  CHANGE_PASSWORD_FAILED: 'Unable to change password, please try again',
  EDIT_PROFILE_FAILED: 'Unable to edit profile, please try again',
  LOGIN_FAILED: 'Login failed.',
}

export const LABEL = {
  CLIENT_NAME: 'Client Name',
  CONFIRM_NEW_PASSWORD: 'Confirm New Password',
  CURRENT_PASSWORD: 'Current Password',
  DISMISS: 'Dismiss',
  DROPBOX_NAME: 'DROPBOX NAME',
  EMAIL: 'Email',
  FIRST_NAME: 'First Name',
  HOME: 'Home',
  IS_CONSULTANT: 'IS A CONSULTANT',
  LAST_NAME: 'Last Name',
  NEW_PASSWORD: 'New Password',
  PASSWORD: 'Password',
  PASSWORD_CONFIRMATION: 'Password Confirmation',
  ROLE: 'Role',
  SHOW_INACTIVE_CLIENTS: 'Show Inactive Clients',
  SHOW_INACTIVE_USERS: 'Show Inactive Users',
  SUB_PLANS: 'Sub Plans',
  THIS_PLAN: 'This Plan',
  USER_PROFILE: 'My Profile',
}

export const MESSAGE = {
  ADD_DOMAIN_FAILURE: 'Problem adding new domain to whitelist',
  ADD_DOMAIN_NOT_ENOUGH_INFO:
    'Missing client information for update, please refresh the page and try again',
  ADD_DOMAIN_SUCCESS: 'Successfully added new email domain to whitelist',
  CLIENT_CREATE_FAILURE:
    'There was an issue creating the client. Please review your input and try again.',
  CLIENT_CREATE_SUCCESS: 'Client successfully created.',
  CLIENT_UPDATE_FAILURE:
    'There was an issue updating the client information. Please review your changes and try again.',
  CLIENT_UPDATE_SUCCESS: 'Client successfully updated.',
  DOCUMENT_CENTER_ASSIGN_FAILURE: 'Unable to assign documents',
  DOCUMENT_CENTER_DOWNLOAD_FAILURE:
    'Unable to download files from document center',
  DOCUMENT_CENTER_DOWNLOAD_PLURAL_STARTED: 'Gathering your files',
  DOCUMENT_CENTER_DOWNLOAD_PLURAL_SUCCESS:
    'Your files are now being downloaded. Please check your browser for progress of the download.',
  DOCUMENT_CENTER_DOWNLOAD_SINGULAR_STARTED: 'Gathering your file',
  DOCUMENT_CENTER_DOWNLOAD_SINGULAR_SUCCESS:
    'Your file is now being downloaded. Please check your browser for progress of the download.',
  DOCUMENT_CENTER_UPDATE_UNASSIGNED_CLIENT_FAILURE:
    'Unassigned client account failed to update',
  DOCUMENT_CENTER_UPDATE_UNASSIGNED_RECIPIENT_FAILURE:
    'Unassigned recipient failed to update',
  DOCUMENT_CENTER_UPLOAD_FAILURE: 'Unable to upload files to document center',
  IMAGE_FILE_SIZE_ERROR: 'Image size must be 2MB or below.',
  INPUT_REQUIRED: 'Required',
  INVALID_EMAIL_ADDRESS: 'Email address is not a valid format',
  INVALID_PASSWORD: 'Invalid password format',
  LEVEL_ANALYSIS_CHANGE_FAILURE: 'Error: Unable to change Level of Analysis',
  PASSWORDS_MUST_MATCH:
    'The passwords entered above do not match. Please re-enter.',
  PASSWORD_LOWERCASE: 'Password must contain at least one lowercase letter',
  PASSWORD_MIN_LENGTH: 'Password must be at least 8 characters long',
  PASSWORD_NUMBER: 'Password must contain at least one number',
  PASSWORD_SPECIAL_CHAR: 'Password must contain at least one special character',
  PASSWORD_UPPERCASE: 'Password must contain at least one uppercase letter',
  PLEASE_SELECT_IMAGE: 'Please select an image.',
  REPORT_GROUPS_SAVE_FAILURE: 'Report groups failed to save',
  REPORT_GROUPS_SAVE_SUCCESS: 'Report groups saved',
  REPORT_NARRATIVE_SELECTED:
    'Narrative documents only support PDF format.  Please change the export format, or unselect the narratives to export',
  REPORT_PERMISSIONS_FAILURE: 'Report permissions failed to save',
  REPORT_PERMISSIONS_SUCCESS: 'Report permissions saved',
  REPORT_SECTION_FAILURE:
    'The information could not be accessed at this time. Please try again.',
  REQUEST_NEW_LINK_FAILED: 'Sending new link failed. Please try again.',
  REQUEST_NEW_LINK_SENT: 'A new link has been sent.',
  REQUEST_REGISTRATION_LINK_FAILED:
    'Sending registration link failed. Please try again.',
  REQUEST_REGISTRATION_LINK_SENT: 'A registration link has been sent.',
  RESET_PASSWORD_EMAIL_SENT: 'A reset password email has been sent.',
  RESET_PASSWORD_FAILED: 'Password reset error. Please try again.',
  SERVICE_DELIVERABLES_CREATE_PAGE_FAILURE: 'Unable to create page',
  SERVICE_DELIVERABLES_CREATE_SECTION_FAILURE: 'Unable to create section',
  SERVICE_DELIVERABLES_DELETE_FILE_FAILURE: 'Unable to delete file',
  SERVICE_DELIVERABLES_DELETE_PAGE_FAILURE: 'Unable to delete page',
  SERVICE_DELIVERABLES_DELETE_SECTION_FAILURE: 'Unable to delete section',
  SERVICE_DELIVERABLES_EDIT_FILE_FAILURE: 'Unable to edit file name',
  SERVICE_DELIVERABLES_EDIT_FILE_SUCCESS: 'The file was successfully renamed',
  SERVICE_DELIVERABLES_EDIT_PAGE_FAILURE: 'Unable to edit page name',
  SERVICE_DELIVERABLES_EDIT_PAGE_SUCCESS: 'The page was successfully renamed',
  SERVICE_DELIVERABLES_EDIT_SECTION_FAILURE: 'Unable to edit section name',
  SERVICE_DELIVERABLES_EDIT_SECTION_SUCCESS:
    'The section was successfully renamed',
  SERVICE_DELIVERABLES_GET_PAGES_FAILURE: 'Unable to get pages',
  SERVICE_DELIVERABLES_GET_SECTIONS_FILES_FAILURE:
    'Unable to get sections and files',
  SERVICE_DELIVERABLES_SAVE_PAGE_CHANGES_FAILURE:
    'Unable to save page order or publish changes',
  SERVICE_DELIVERABLES_SAVE_SECTION_FILES_CHANGES_FAILURE:
    'Unable to save section and file order changes',
  SUPPORT_ACCESS_CLIENT_DENY_SUCCESS: 'Access successfully denied.',
  SUPPORT_ACCESS_CLIENT_GRANTED_SUCCESS: 'Access successfully granted.',
  SUPPORT_ACCESS_REMOVED_ACCESS_SUCCESS: 'Access successfully removed.',
  SYSTEM_ALERT_GET_UNREAD_FAILURE:
    'Something went wrong getting your unread system alerts',
  UPDATE_PLAN_SELECTED_SETTINGS:
    'An update plan is currently selected and will not report on annual goals.',
  UPLOAD_FAILED: 'Upload failed. Please try again.',
  USER_ACCOUNT_ACTIVATED:
    'Your account has been activated. Please login below.',
  USER_CREATE_SUCCESS:
    'User successfully created. A welcome email has been sent.',
  USER_INACTIVE_SUBMIT_FAILURE: `User is currently inactive. Please <a class="underline" href="/dashboard/users/manage">set user to active</a> to complete update.`,
  USER_PASSWORD_UPDATE_FAILURE: 'User password failed to update',
  USER_PASSWORD_UPDATE_SUCCESS: 'User password updated',
  USER_UPDATE_SUCCESS: 'Changes successfully saved',
  WELCOME_FORM_FAILED: 'Welcome form error. Please try again.',
}

export const NAME = {
  AUDIT_CHECKLIST: 'audit-checklist',
  MANAGE_REPORTS: 'manage-reports',
  REPORTS: 'serviceLine-reports',
  SUMMARIES: 'summaries',
} as const

export const PATH = {
  AAP_DATA_VIZ_OVERVIEW: '/compliance/data-viz/overview',
  AAP_GLOBAL_SETTINGS: '/compliance/global-settings',
  AAP_MANAGE_REPORTS: '/compliance/reports/manage',
  AAP_MANAGE_SERVICE_DELIVERABLES: '/compliance/service-deliverables/manage',
  AAP_REPORTS: '/compliance/reports',
  AAP_SUMMARIES: '/compliance/summaries',
  ADMIN_SUPPORT_ACCESS: '/help/admin-support-access',
  CLIENT_MANAGEMENT: '/dashboard/clients/manage',
  CLIENT_SUPPORT_ACCESS: '/help/client-support-access',
  CONTACT_SUPPORT: '/help/contact-support',
  CREATE_CLIENT: '/dashboard/clients/create',
  CREATE_USER: '/dashboard/users/create',
  DASHBOARD: '/dashboard',
  DOCUMENT_CENTER: '/document-center',
  DOCUMENT_CENTER_PUBLIC_UPLOAD: '/document-center/upload',
  EDIT_CLIENT: '/dashboard/clients/edit',
  EDIT_USER: '/dashboard/users/edit',
  INVALID_LINK: '/invalid-link',
  LOGIN: '/login',
  LOGIN_MFA: '/login-mfa',
  MANAGE_CLIENT_INFORMATION: '/manage/client-information',
  MANAGE_FEATURE_FLAGS: '/manage/feature-flags',
  MANAGE_PROJECTS_LOCATIONS: '/manage/projects-and-locations',
  MANAGE_SECURITY: '/manage/security',
  MANAGE_SYSTEM_ALERTS: '/system-alerts/manage',
  NOTIFICATIONS: '/notifications',
  PASSWORD_EXPIRED: '/password-expired',
  REGISTRATION_REQUIRED: '/registration-required',
  REPORTS_MANAGEMENT: '/manage-reports',
  REPORT_PREVIEW: '/report-preview',
  RESET_PASSWORD: '/reset-password',
  RESOURCE_LIBRARY: '/resource-library',
  RESOURCE_LIBRARY_PREVIEW: '/resource-library/preview',
  ROOT: '/',
  SECURITY_SETTINGS: '/settings/security',
  SSO_FAILED: '/sso-failed',
  SUBDOMAIN_REDIRECTION: '/subdomain-redirection',
  SUPPORT_ACCESS: '/help/support-access',
  SWITCH_CLIENT: '/switch-client',
  USER_MANAGEMENT: '/dashboard/users/manage',
  USER_PROFILE: '/profile',
  VIEW_AS: '/view-as',
  WELCOME: '/welcome',
} as const satisfies Record<string, `/${string}`>

export const CHART_PLUGIN = {
  CLICK_LABELS: 'clickLabelsPlugin',
  LABEL_TOOLTIPS: 'labelTooltipsPlugin',
  NO_ISSUES: 'noIssuesPlugin',
}

export const PLACEHOLDER = {
  SEARCH: 'Search',
}

export const STORE = {
  AAP_EXPORT_REPORTS: 'aap-export-reports',
  AAP_PREVIEW_REPORTS: 'aap-preview-report',
  AAP_REPORTS: 'aap-reports',
  ADVERSE_IMPACT_DATA_VISUALIZATION: 'adverse-impact-data-visualization',
  APPLICANT_FLOW_DATA_VISUALIZATION: 'applicant-flow-data-visualization',
  AUTH: 'auth',
  CLIENT: 'client',
  CLIENT_MANAGEMENT: 'client-management',
  COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS: 'competitive-movements',
  DATA_VISUALIZATIONS: 'data-visualizations',
  DATA_VISUALIZATIONS_DRILL_DOWN: 'data-viz-drill-downs',
  DATA_VISUALIZATIONS_OVERVIEW: 'data-viz-overview',
  EMPLOYMENT_DATA_VISUALIZATION: 'employment-data-visualization',
  EXECUTIVE_SUMMARY: 'executive-summary',
  FEATURE_FLAGS: 'featureFlags',
  GOALS_DATA_VISUALIZATION: 'goals-data-visualization',
  GOAL_ATTAINMENT_DATA_VISUALIZATION: 'goal-attainment-data-visualization',
  INDIVIDUALS_WITH_DISABILITIES: 'individuals-with-disabilities',
  LABOR_MARKET_COMPARISONS_DATA_VISUALIZATIONS: 'labor-market-comparisons',
  PERSONNEL_ACTIVITY_DATA_VISUALIZATION:
    'personnel-activity-data-visualization',
  PLANS: 'plans',
  PROJECTS: 'projects',
  PROJECT_APPLICANT_FLOW_DATA_VISUALIZATIONS: 'project-applicant-flow',
  PROJECT_DATA_VISUALIZATIONS: 'project-data-visualizations',
  PROJECT_DATA_VISUALIZATIONS_SELECTIONS:
    'project-data-visualizations-selections',
  PROJECT_OVERVIEW_DATA_VISUALIZATIONS: 'project-overview',
  PROPORTIONAL_ANALYSES_DATA_VISUALIZATIONS: 'proportional-analyses',
  PROTECTED_VETERANS: 'protected-veterans',
  REPORT: 'report',
  REPORT_SETTINGS: 'report-settings',
  SUBPLANS: 'subplans',
  SUPPORT_ACCESS_ADMIN: 'support-access-admin',
  SUPPORT_ACCESS_CLIENT: 'support-access-client',
  SYSTEM_ALERTS_ADMIN: 'system-alerts-admin',
  SYSTEM_ALERTS_CLIENT: 'system-alerts-client',
  THEME: 'theme',
  USER: 'user',
  WORKFORCE_REPRESENTATION_DATA_VISUALIZATIONS: 'workforce-representation',
}

export const ROUTE_NAME = {
  COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS: 'competitive-movements',
  NON_COMPETITIVE_MOVEMENTS_DATA_VISUALIZATIONS: 'non-competitive-movements',
  PROJECT_OVERVIEW_DATA_VISUALIZATIONS: 'project-overview',
  RECRUITMENT_PROCESS_INSIGHTS_DATA_VISUALIZATIONS:
    'recruitment-process-insights',
  TALENT_AVAILABILITY_METRICS_DATA_VISUALIZATIONS:
    'talent-availability-metrics',
  WORKFORCE_INSIGHTS_DATA_VISUALIZATIONS: 'workforce-insights',
} as const satisfies Record<string, keyof RouteNamedMap>

export const USER_FACING_ERROR_MESSAGES = {
  GENERIC: 'An error occurred.',
  SERVER_ERROR: 'Something went wrong.',
}

export const TITLE = {
  AVAILABLE_REPORTS: 'Available Reports',
  BY_PLAN: 'by Plan',
  BY_RACE_AND_GENDER: 'BY RACE & GENDER',
  CLIENTS_TABLE: 'Clients',
  CLIENT_MANAGEMENT: 'Manage Clients',
  CLIENT_PORTAL: 'CLIENT PORTAL',
  CLIENT_PORTAL_LOGIN: 'CLIENT PORTAL LOGIN',
  CONTACT_SUPPORT: 'Contact Support',
  CREATE_NEW_CLIENT: 'Create New Client',
  CREATE_USER: 'Create New User',
  DOCUMENT_CENTER: 'Document Center',
  EDIT_CLIENT: 'Edit Client',
  EDIT_USER: 'Edit User',
  ENABLE_SERVICE_LINES: 'Enable Service Lines',
  EXPORTING_REPORTS: 'Exporting Reports for...',
  HAVE_BEEN_LOGGED_OUT: 'You have been logged out',
  MANAGE_REPORTS: 'Manage Reports',
  MANAGE_SERVICE_DELIVERABLES: 'Manage Deliverables',
  NOTIFICATION_PREFERENCES: 'Notification Preferences',
  PASSWORD_EXPIRED: 'PASSWORD EXPIRED',
  PROFILE: 'My Profile',
  RESET_PASSWORD: 'RESET PASSWORD',
  SECURITY: 'Security',
  SECURITY_SETTINGS: 'Security Settings',
  SETTINGS: 'Settings',
  SWITCH_CLIENT: 'Switch Client',
  UPLOAD_CLIENT_LOGO: 'Upload Client Logo',
  USER_MANAGEMENT: 'Manage Users',
  USER_TABLE: 'Users',
}

export const URL = {
  BERKSHIRE_ASSOCIATES: 'https://www.berkshireassociates.com/',
  BERKSHIRE_ASSOCIATES_DOMAIN: 'berkshireassociates.com',
  DOCUMENTATION_SITE: 'https://berkshire.helpjuice.com',
  PRIVACY_POLICY: 'https://works.baiworks.com/assets/privacy/policy.pdf',
}
